import { getAccessToken } from '../auth/keycloak';
import { currentLanguage } from '../i18n';
import { ApiError, type ProblemDetail } from './errors';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** A body that already is JSON text (e.g. the content of an uploaded file), sent as it is instead of `body`. */
  json?: string;
  /** Optimistic-lock version, sent as If-Match. */
  version?: number;
  query?: Record<string, string | number | boolean | undefined | null | (string | number)[]>;
  signal?: AbortSignal;
}

export function buildQuery(query: RequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) value.forEach((v) => params.append(key, String(v)));
    else params.append(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function headers(options: RequestOptions): Promise<Headers> {
  const h = new Headers();
  h.set('Accept', 'application/json, application/problem+json');
  h.set('Accept-Language', currentLanguage());
  h.set('Authorization', `Bearer ${await getAccessToken()}`);
  if (options.body !== undefined || options.json !== undefined) h.set('Content-Type', 'application/json');
  if (options.version !== undefined) h.set('If-Match', `"${options.version}"`);
  return h;
}

async function toError(response: Response): Promise<ApiError> {
  let problem: ProblemDetail = { status: response.status };
  try {
    const text = await response.text();
    if (text) problem = { ...problem, ...(JSON.parse(text) as ProblemDetail) };
  } catch {
    // non-JSON error body
  }
  return new ApiError(problem);
}

/** JSON request against /api/v1 with bearer token, UI language and problem+json error handling. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(`/api/v1${path}${buildQuery(options.query)}`, {
    method: options.method ?? 'GET',
    headers: await headers(options),
    body: options.json ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
    signal: options.signal,
  });
  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Downloads a file (PDF/DOCX document, JSON template file) and lets the browser save it. */
export async function download(path: string, query: RequestOptions['query'], fallbackName: string): Promise<void> {
  const h = await headers({});
  h.set('Accept', 'application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/json, application/problem+json');
  const response = await fetch(`/api/v1${path}${buildQuery(query)}`, { headers: h });
  if (!response.ok) throw await toError(response);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
  const name = match ? decodeURIComponent(match[1] ?? match[2] ?? fallbackName) : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Public endpoint (no token). */
export async function publicApi<T>(path: string): Promise<T> {
  const response = await fetch(`/api/v1${path}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw await toError(response);
  return (await response.json()) as T;
}
